import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, Loader2, Home } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking');
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [copied, setCopied] = useState(false);
  const sessionId = searchParams.get('session_id');

  const pollPaymentStatus = useCallback(async (sessionId, attempts) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('error');
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/checkout/status/${sessionId}`);
      const data = response.data;

      if (data.payment_status === 'paid') {
        setStatus('success');
        setPaymentDetails(data);
        return;
      } else if (data.status === 'expired') {
        setStatus('expired');
        return;
      }

      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setStatus('error');
    }
  }, []);

  useEffect(() => {
    if (!sessionId) {
      navigate('/');
      return;
    }

    pollPaymentStatus(sessionId, 0);
  }, [sessionId, navigate, pollPaymentStatus]);

  const referralCode = paymentDetails?.metadata?.referral_code;
  const referralLink = referralCode ? `${window.location.origin}/?ref=${referralCode}` : '';

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-12 bg-[#0B0512] text-[#F8F5EE]" data-testid="payment-success-page">
      <Card className="payment-success-card max-w-2xl w-full border border-[#D4AF37]/30 bg-[#140924]/95 text-[#F8F5EE] shadow-[0_30px_100px_rgba(0,0,0,0.45)]">
        <CardContent className="p-12 text-center">
          {status === 'checking' && (
            <>
              <Loader2 className="w-16 h-16 mx-auto mb-6 text-purple-600 animate-spin" />
              <h1 className="text-3xl font-bold mb-4">Processing Your Payment</h1>
              <p className="text-gray-600">Please wait while we confirm your reservation...</p>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircle2 className="w-20 h-20 mx-auto mb-6 text-green-600" />
              <h1 className="text-4xl font-bold mb-4 text-green-600">Payment Successful!</h1>
              <p className="text-xl text-gray-700 mb-6">
                Welcome to Siege's Playcation!
              </p>
              <div className="bg-gradient-to-r from-purple-50 to-amber-50 rounded-lg p-6 mb-8 text-left">
                <h2 className="text-xl font-bold mb-4">What's Next?</h2>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Check your email for booking confirmation and receipt</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>King will contact you within 24 hours with trip details</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Final payment due 30 days before departure (June 16, 2027)</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Start planning what to pack for paradise</span>
                  </li>
                </ul>
              </div>
              {paymentDetails && (
                <div className="text-sm text-gray-600 mb-6">
                  <p>Transaction ID: {paymentDetails.session_id}</p>
                  <p className="mt-1">Amount: ${(paymentDetails.amount_total / 100).toFixed(2)} {paymentDetails.currency.toUpperCase()}</p>
                </div>
              )}
              {referralLink && (
                <div className="referral-share-box" data-testid="referral-share-box">
                  <p>Share your private referral link:</p>
                  <div>
                    <code data-testid="referral-link-text">{referralLink}</code>
                    <Button
                      type="button"
                      variant="outline"
                      data-testid="copy-referral-link-button"
                      onClick={async () => {
                        await navigator.clipboard.writeText(referralLink);
                        setCopied(true);
                      }}
                    >
                      {copied ? 'Copied' : 'Copy'}
                    </Button>
                  </div>
                </div>
              )}
              <p className="text-gray-600 mb-8">
                Questions? Text King: <a href="sms:8325965177" className="font-bold text-purple-700 hover:text-purple-900">832-596-5177</a>
                <br />
                Or email: <a href="mailto:siegeplaycations@gmail.com" className="font-bold text-purple-700 hover:text-purple-900">siegeplaycations@gmail.com</a>
              </p>
              <Button
                size="lg"
                onClick={() => navigate('/')}
                className="cta-primary"
              >
                <Home className="w-5 h-5 mr-2" />
                Back to Home
              </Button>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-20 h-20 mx-auto mb-6 bg-red-100 rounded-full flex items-center justify-center">
                <span className="text-4xl">×</span>
              </div>
              <h1 className="text-3xl font-bold mb-4 text-red-600">Payment Status Check Failed</h1>
              <p className="text-gray-600 mb-8">
                We're having trouble confirming your payment. Please check your email for confirmation or contact King.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button variant="outline" onClick={() => navigate('/')}>
                  Back to Home
                </Button>
                <Button onClick={() => window.location.href = 'sms:8325965177'}>
                  Contact King
                </Button>
              </div>
            </>
          )}

          {status === 'expired' && (
            <>
              <div className="w-20 h-20 mx-auto mb-6 bg-yellow-100 rounded-full flex items-center justify-center">
                <span className="text-4xl">!</span>
              </div>
              <h1 className="text-3xl font-bold mb-4 text-yellow-600">Session Expired</h1>
              <p className="text-gray-600 mb-8">
                Your payment session has expired. Please try booking again.
              </p>
              <Button onClick={() => navigate('/')} className="cta-primary">
                Try Again
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccess;
